import type { AffineMatrix, PointMm } from '../schema/primitives.js';

/**
 * CONVENIO DE ESPACIOS (decision de implementacion, ver DECISIONES-IMPLEMENTACION.md D-I-01)
 *
 * La matriz afin de una capa mapea su ESPACIO LOCAL al espacio de la ZONA DE
 * MARCAJE, en milimetros. El espacio local depende del tipo de capa:
 *
 *  - `image`: rectangulo UNIDAD [0,1] x [0,1]. Asi `a` y `d` son directamente el
 *    ancho y el alto en mm cuando no hay rotacion, y la resolucion efectiva sale
 *    de dividir los pixeles intrinsecos entre esos mm.
 *  - `shape`: el path YA esta en mm; la matriz es una transformacion adicional
 *    (normalmente identidad, o rotacion/traslacion).
 *  - `text`: mm (sizeMm, lineHeightMm); la matriz es una transformacion adicional.
 *
 * Sin este convenio escrito, "a: 80" no significa nada.
 */

export function multiply(m1: AffineMatrix, m2: AffineMatrix): AffineMatrix {
  return {
    a: m1.a * m2.a + m1.c * m2.b,
    b: m1.b * m2.a + m1.d * m2.b,
    c: m1.a * m2.c + m1.c * m2.d,
    d: m1.b * m2.c + m1.d * m2.d,
    e: m1.a * m2.e + m1.c * m2.f + m1.e,
    f: m1.b * m2.e + m1.d * m2.f + m1.f,
  };
}

export function applyPoint(m: AffineMatrix, p: PointMm): PointMm {
  return { x: m.a * p.x + m.c * p.y + m.e, y: m.b * p.x + m.d * p.y + m.f };
}

/** Aplica solo la parte lineal (sin traslacion): para vectores, no puntos. */
export function applyVector(m: AffineMatrix, p: PointMm): PointMm {
  return { x: m.a * p.x + m.c * p.y, y: m.b * p.x + m.d * p.y };
}

export function determinant(m: AffineMatrix): number {
  return m.a * m.d - m.b * m.c;
}

export function invert(m: AffineMatrix): AffineMatrix {
  const det = determinant(m);
  if (det === 0) throw new Error('matriz afin no invertible (determinante 0)');
  return {
    a: m.d / det,
    b: -m.b / det,
    c: -m.c / det,
    d: m.a / det,
    e: (m.c * m.f - m.d * m.e) / det,
    f: (m.b * m.e - m.a * m.f) / det,
  };
}

export interface Decomposed {
  translateX: number;
  translateY: number;
  rotationDeg: number;
  scaleX: number;
  scaleY: number;
  skewXDeg: number;
  /** true si la matriz invierte la orientacion (espejado). */
  mirrored: boolean;
}

export function decompose(m: AffineMatrix): Decomposed {
  const scaleX = Math.hypot(m.a, m.b);
  const det = determinant(m);
  const scaleYSigned = scaleX === 0 ? 0 : det / scaleX;
  const rotation = Math.atan2(m.b, m.a);
  const skew = scaleX === 0 ? 0 : Math.atan2(m.a * m.c + m.b * m.d, scaleX * scaleX);
  return {
    translateX: m.e,
    translateY: m.f,
    rotationDeg: (rotation * 180) / Math.PI,
    scaleX,
    scaleY: Math.abs(scaleYSigned),
    skewXDeg: (skew * 180) / Math.PI,
    mirrored: det < 0,
  };
}

export function fromTranslation(x: number, y: number): AffineMatrix {
  return { a: 1, b: 0, c: 0, d: 1, e: x, f: y };
}

export function fromScale(sx: number, sy: number): AffineMatrix {
  return { a: sx, b: 0, c: 0, d: sy, e: 0, f: 0 };
}

export function fromRotationDeg(deg: number): AffineMatrix {
  const r = (deg * Math.PI) / 180;
  const cos = Math.cos(r);
  const sin = Math.sin(r);
  return { a: cos, b: sin, c: -sin, d: cos, e: 0, f: 0 };
}
